import { getEventReminderStatus } from '@shared/core/events'
import type { EventReminderStatus } from '@shared/core/events'
import { UserDtoGoogleCalendarDateOrderEnum } from '@shared/core/users'
import classNames from 'classnames'
import { format } from 'date-fns'
import { Sparkles } from 'lucide-react'
import Tooltip from 'rc-tooltip'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { getEventDate } from 'src/helpers/get-event-date'
import { Typography } from 'src/modules/core/components'
import { createRcTooltipPlacements, RC_TOOLTIP_GAP_PX } from 'src/modules/core/utils/create-rc-tooltip-placements'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { EventPreviewWithReminderData } from '../../types/event.types'
import { EventAutoActivationHelper } from '../../utils/EventAutoActivationHelper'
import { formatEventDate } from '../../utils/helpers'
import {
  buildMessagesSentAndNext,
  MessageSentAndNext,
  useWasEventRecentlyActivated,
} from '../../utils/recently-activated-events'
import classes from './event-card-status.module.scss'

export type EventCardStatusProps = {
  event: EventPreviewWithReminderData
  isError?: boolean
  /**
   * In some cases, the Event Card is used to display reminders,
   * and in that case the event_date is already the reminder date.
   */
  eventDateIsReminderDate?: boolean
}

export function EventCardStatus({ event, isError, eventDateIsReminderDate }: EventCardStatusProps) {
  const { data: profileData } = useProfile()
  const { formatMessage } = useIntl()
  const recentlyActivated = useWasEventRecentlyActivated(event.id)
  const hasAutoActivationMaterial = EventAutoActivationHelper.hasAutoActivationMaterial(event)
  const autoActivationReason = useMemo(
    () =>
      EventAutoActivationHelper.formatReason(
        event.auto_activation_reason_code,
        event.auto_activation_reason_params,
        formatMessage,
      ),
    [event.auto_activation_reason_code, event.auto_activation_reason_params, formatMessage],
  )

  // Tooltip copy summarizing sent/next messages. Not part of the status value.
  const tooltipTitle = useMemo(() => {
    if (eventDateIsReminderDate) {
      return undefined
    }
    const eventDate = getEventDate(event)
    const messagesSentAndNext = buildMessagesSentAndNext(event, eventDate)
    return buildMessageTitle(messagesSentAndNext, profileData?.google_calendar_date_order ?? null)
  }, [event, eventDateIsReminderDate, profileData])

  const status = getEventReminderStatus(event, {
    now: Date.now(),
    isError,
    recentlyActivated,
    eventDateIsReminderDate,
    remindersPaused: profileData?.reminders_paused,
  })

  const cssClass = classNames(classes.status, {
    [classes.statusError]: status === 'error',
    [classes.statusInactive]: status === 'inactive' || status === 'activating',
    [classes.statusNoReminder]: status === 'no_reminder',
    [classes.statusReview]: status === 'review',
    [classes.statusScheduled]: status === 'scheduled',
    [classes.statusSent]: status === 'sent',
  })

  const label = getStatusLabel(status, formatMessage)

  return (
    <div className={classes.statusRow}>
      <span className={classNames(classes.status, cssClass)} title={tooltipTitle}>
        {label}
      </span>
      {hasAutoActivationMaterial && (
        <Tooltip
          placement="top"
          builtinPlacements={createRcTooltipPlacements(RC_TOOLTIP_GAP_PX)}
          trigger={['hover', 'focus']}
          overlay={
            <Typography variant="body" className={classes.autoActivationTooltipText}>
              {autoActivationReason}
            </Typography>
          }
          overlayClassName={classes.autoActivationTooltipOverlay}
        >
          <span
            className={classes.autoActivationSparkles}
            tabIndex={0}
            role="img"
            aria-label={autoActivationReason}
            data-testid="auto-activation-sparkles"
          >
            <Sparkles size={14} aria-hidden="true" />
          </span>
        </Tooltip>
      )}
    </div>
  )
}

function getStatusLabel(status: EventReminderStatus, formatMessage: IntlShape['formatMessage']): string {
  if (status === 'activating') return 'Activating...'
  if (status === 'no_reminder') return formatMessage({ id: 'event.paused.status.noReminder' })
  return status
}

function buildMessageTitle(messagesSentAndNext: MessageSentAndNext, dateOrder: string | null): string {
  const formatDate = (date: string) =>
    formatEventDate(new Date(date), (dateOrder as UserDtoGoogleCalendarDateOrderEnum) || 'MDY')
  const formatTime = (date: string) => format(new Date(date), 'h:mma')

  const messageTexts = []

  // Confirmation messages
  if (messagesSentAndNext.lastConfirmation) {
    let text = `Last Confirmation message sent on ${formatDate(messagesSentAndNext.lastConfirmation)} at ${formatTime(
      messagesSentAndNext.lastConfirmation,
    )}.`
    if (messagesSentAndNext.nextConfirmation) {
      text += ` Next on ${formatDate(messagesSentAndNext.nextConfirmation)} at ${formatTime(
        messagesSentAndNext.nextConfirmation,
      )}.`
    }
    messageTexts.push(text)
  } else if (messagesSentAndNext.nextConfirmation) {
    messageTexts.push(
      `Next Confirmation message on ${formatDate(messagesSentAndNext.nextConfirmation)} at ${formatTime(
        messagesSentAndNext.nextConfirmation,
      )}.`,
    )
  }

  // Reminder messages
  if (messagesSentAndNext.lastReminder) {
    let text = `Last Reminder message sent on ${formatDate(messagesSentAndNext.lastReminder)} at ${formatTime(
      messagesSentAndNext.lastReminder,
    )}.`
    if (messagesSentAndNext.nextReminder) {
      text += ` Next on ${formatDate(messagesSentAndNext.nextReminder)} at ${formatTime(
        messagesSentAndNext.nextReminder,
      )}.`
    }
    messageTexts.push(text)
  } else if (messagesSentAndNext.nextReminder) {
    messageTexts.push(
      `Next Reminder message on ${formatDate(messagesSentAndNext.nextReminder)} at ${formatTime(
        messagesSentAndNext.nextReminder,
      )}.`,
    )
  }

  // Follow-up messages
  if (messagesSentAndNext.lastFollowUp) {
    let text = `Last Follow-up message sent on ${formatDate(messagesSentAndNext.lastFollowUp)} at ${formatTime(
      messagesSentAndNext.lastFollowUp,
    )}.`
    if (messagesSentAndNext.nextFollowUp) {
      text += ` Next on ${formatDate(messagesSentAndNext.nextFollowUp)} at ${formatTime(
        messagesSentAndNext.nextFollowUp,
      )}.`
    }
    messageTexts.push(text)
  } else if (messagesSentAndNext.nextFollowUp) {
    messageTexts.push(
      `Next Follow-up message on ${formatDate(messagesSentAndNext.nextFollowUp)} at ${formatTime(
        messagesSentAndNext.nextFollowUp,
      )}.`,
    )
  }

  return messageTexts.join('\n')
}
